<template>
  <v-container>
    <v-toolbar flat color="white" elevation="1">
      <v-select
          v-model="producerTypeId"
          :items="$store.state.producer_types.items"
          item-title="name"
          item-value="pk"
          :label="$filters.capitalize($t('producer_types.label'))"
          :loading="$store.state.producer_types.isLoading"
          hide-details
          class="mr-4"
          style="max-width: 300px"
          ></v-select>
      <v-text-field
          v-model="search"
          append-icon="mdi-folder-search"
          :label="$filters.capitalize($t('search'))"
          single-line
          hide-details
          ></v-text-field>
      <v-spacer></v-spacer>
      <v-toolbar-title style="font-weight: bolder">
        {{ $filters.capitalize($t('verif_machines.label', 10)) }}
      </v-toolbar-title>
      <v-spacer></v-spacer>
      <EditDialog
          :model="editDialog"
          :moduleName="moduleName"
          :btnName="$t('verif_machines.actions.add_item')"
          :formTitle="editedItem.pk === undefined ? $t('new_item') : $t('edit_item')"
          :disabled="!producerTypeId"
          :dataValidation="validate"
          @add="openAdd"
          @save="save"
          @close="closeDialog"
          ref="editDialogRef"
          >
          <template v-slot:edit_content>
            <v-row>
              <v-col cols="12" sm="6">
                <v-autocomplete
                    v-model="editedItem.verification_category_id"
                    :items="$store.state.verif_categories.items"
                    item-title="name"
                    item-value="pk"
                    :label="$filters.capitalize($t('verif_machines.category'))"
                    :loading="$store.state.verif_categories.isSearching"
                    variant="solo"
                    ></v-autocomplete>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                    v-model="editedItem.period_months"
                    type="number"
                    min="1"
                    :label="$filters.capitalize($t('verif_machines.period_months'))"
                    ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                    v-model="editedItem.verification_title"
                    :label="$filters.capitalize($t('verif_machines.title'))"
                    ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                    v-model="editedItem.verification_title_en"
                    :label="$filters.capitalize($t('verif_machines.title_en'))"
                    ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-textarea
                    v-model="editedItem.indications"
                    rows="2"
                    auto-grow
                    :label="$filters.capitalize($t('verif_machines.indications'))"
                    ></v-textarea>
              </v-col>
              <v-col cols="12" sm="6">
                <v-textarea
                    v-model="editedItem.indications_en"
                    rows="2"
                    auto-grow
                    :label="$filters.capitalize($t('verif_machines.indications_en'))"
                    ></v-textarea>
              </v-col>
              <v-col cols="12">
                <v-select
                    v-model="editedItem.producer_ids"
                    :items="producersOfType"
                    item-title="display_name"
                    item-value="pk"
                    :label="$filters.capitalize($t('producers.label', 10))"
                    :hint="$filters.capitalize($t('verif_machines.producers_hint'))"
                    persistent-hint
                    variant="solo"
                    multiple
                    chips
                    clearable
                    ></v-select>
              </v-col>
            </v-row>
          </template>
      </EditDialog>
    </v-toolbar>

    <v-data-table
        :headers="headers"
        :items="items"
        :search="search"
        :loading="$store.state.verif_machines.isLoading"
        :group-by="[{ key: 'category', order: 'asc' }]"
        :no-data-text="$filters.capitalize($t(producerTypeId ? 'no_data_text' : 'verif_machines.pick_type'))"
        :items-per-page="-1"
        hide-default-footer
        fixed-header
        >
        <template #group-header="{ item, columns, toggleGroup, isGroupOpen }">
          {{ isGroupOpen(item) ? "" : toggleGroup(item) }}
          <tr>
            <td :colspan="columns.length" class="bg-blue-lighten-5 font-weight-bold">
              {{ item.value }}
            </td>
          </tr>
        </template>
        <template #item.period_months="{ item }">
          {{ $t('verif_machines.every_n_months', { n: item.period_months }) }}
        </template>
        <template #item.producers="{ item }">
          <v-chip v-for="p in item.producers" :key="p.pk" size="small" class="mr-1">
            {{ p.display_name }}
          </v-chip>
          <span v-if="!item.producers.length" class="text-grey">
            {{ $t('verif_machines.all_producers') }}
          </span>
        </template>
        <template #item.actions="{ item }">
          <v-icon size="small" class="mr-1" @click="edit(item)">mdi-pencil</v-icon>
          <v-icon size="small" @click="disable(item)">mdi-delete</v-icon>
        </template>
    </v-data-table>
  </v-container>
</template>

<script>
  import EditDialog from "@/components/EditDialog";
  import { alertPopup } from "@/functions";

  export default {
    components: { EditDialog },
    data() {
      return {
        moduleName: "verif_machines",
        producerTypeId: null,
        search: "",
        editDialog: false,
      }
    },
    computed: {
      editedItem() {
        return this.$store.getters.dataEdit(this.moduleName);
      },
      items() {
        // flat 'category' key so the table can group on the localized name
        return this.$store.state.verif_machines.items.map(i => ({
          ...i, category: this.localized(i.verification_category, 'name'),
        }))
      },
      producersOfType() {
        return this.$store.state.producers.all.filter(p => p.producer_type_id === this.producerTypeId)
      },
      headers() {
        return [
          { title: this.$t("verif_machines.title"), key: "title", value: i => this.localized(i, 'verification_title') },
          { title: this.$t("verif_machines.indications"), key: "indic", value: i => this.localized(i, 'indications') },
          { title: this.$t("verif_machines.periodicity"), key: "period_months" },
          { title: this.$t("producers.label", 10), key: "producers", sortable: false },
          { title: this.$t("actions.label"), key: "actions", sortable: false, align: "end" },
        ]
      },
    },
    watch: {
      producerTypeId() { this.load() },
    },
    mounted() {
      this.$store.dispatch('verif_machines/cleanupItems')
      this.$store.dispatch('producer_types/getItems')
      this.$store.dispatch('verif_categories/getItems')
      this.$store.dispatch('producers/getAll')
    },
    methods: {
      localized(obj, field) {
        if (this.$store.getters.language === 'en' && obj[field + '_en']) return obj[field + '_en']
        return obj[field]
      },
      load() {
        this.$store.dispatch('verif_machines/getItems', { producer_type_id: this.producerTypeId, size: -1 })
      },
      openAdd() {
        this.$store.dispatch('verif_machines/updateEdit',
          { ...this.$store.state.verif_machines.struct, producer_type_id: this.producerTypeId })
        this.editDialog = true
      },
      edit(item) {
        // the serializer already exposes *_id and producer_ids, so the item is the form
        this.$store.dispatch('verif_machines/updateEdit', item)
        this.editDialog = true
      },
      validate() {
        for (const [field, label] of [['verification_category_id', 'category'], ['verification_title', 'title']]) {
          if (!this.editedItem[field]) return this.$t('snack.missing', { field: this.$t('verif_machines.' + label) })
        }
      },
      save() {
        const item = { ...this.editedItem }
        const action = item.pk === undefined ? 'addItem' : 'editItem'
        this.$store.dispatch(`verif_machines/${action}`, item)
          .then(() => { this.closeDialog(); this.load() })
          .catch(e => this.$store.dispatch('snackbar/showError', String(e)))
      },
      disable(item) {
        alertPopup(this.$t('dialog.delete_confirm')).then((ok) => {
          if (ok) this.$store.dispatch('verif_machines/disableItem', item.pk).then(this.load)
        })
      },
      closeDialog() {
        this.$refs.editDialogRef.loadingAddItem = false
        this.editDialog = false
        this.$store.dispatch('verif_machines/cleanupEdit')
      },
    },
  }
</script>
